import React, { useContext, useEffect, useState } from 'react';
import { CheckCircle2, Film, Loader2, RotateCcw, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../User/UserContext';
import { dismissVideoPublication, resumeVideoPublications, cancelVideoPublication, approveVideoPublication } from '../../services/videoPublishingQueue';
import DetailDialog from '../DetailDialog/DetailDialog';
import { useTheme } from '../../contexts/ThemeContext';
import { useVideoPublishingStatus } from '../../hooks/useVideoPublishingStatus';
import './VideoPublishingStatus.css';
import '../CreatePost/VideoAudioOptions.css';

const LABELS = {
  PREPARING: 'جاري تجهيز رفع الفيديو',
  UPLOADING: 'جاري رفع الفيديو',
  QUEUED: 'تم الرفع، بانتظار المعالجة',
  ISOLATING: 'جاري فصل الصوت البشري عن الموسيقى',
  PROCESSING: 'جاري تجهيز الفيديو للنشر',
  REVIEW: 'استمع إلى نتيجة العزل قبل النشر',
  PUBLISHING: 'اكتملت المعالجة، جاري نشر الفيديو',
  READY: 'تم نشر الفيديو',
  FAILED: 'تعذر نشر الفيديو',
};

export default function VideoPublishingStatus() {
  const { userData } = useContext(UserContext);
  const { tasks } = useVideoPublishingStatus(userData?.id);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [reviewOpen, setReviewOpen] = useState(false), [cancelBusy, setCancelBusy] = useState(false), [error, setError] = useState('');
  const task = tasks[0];

  useEffect(() => {
    if (userData?.id) resumeVideoPublications(userData.id);
  }, [userData?.id]);

  if (!task || task.ownerId !== userData?.id) return null;
  const complete = task.stage === 'READY';
  const failed = task.stage === 'FAILED';
  const review = task.stage === 'REVIEW';
  const cancel = async () => { setCancelBusy(true); setError(''); try { await cancelVideoPublication(task.id); setReviewOpen(false); } catch { setError('تعذّر الإلغاء الآن. حاول مجدداً.'); } finally { setCancelBusy(false); } };
  const Icon = complete ? CheckCircle2 : failed ? XCircle : Loader2;

  return (
    <><aside className={`video-publish-toast ${complete ? 'is-ready' : ''} ${failed ? 'is-failed' : ''}`} dir="rtl" aria-live="polite">
      <span className="video-publish-icon"><Icon size={20} className={!complete && !failed && !review ? 'spin' : ''} /></span>
      <button type="button" className="video-publish-copy" onClick={() => review ? setReviewOpen(true) : complete && navigate(`/video/${task.publicId || task.postId}`)}>
        <strong>{LABELS[task.stage] || 'جاري تجهيز الفيديو'}</strong>
        <small>{error || (failed ? task.error : review ? 'لن يُنشر الفيديو قبل مراجعتك وموافقتك. اضغط للاستماع.' : 'يمكنك متابعة استخدام أمبوستر أثناء إكمال العملية.')}</small>
      </button>
      {review && <button type="button" className="video-publish-action" onClick={() => setReviewOpen(true)}>مراجعة</button>}
      {(failed || review) && <button type="button" className="video-publish-action" disabled={cancelBusy} onClick={cancel} aria-label="إلغاء النشر والاحتفاظ بالمسودة"><XCircle size={18}/></button>}
      {failed ? (
        <button type="button" className="video-publish-action" onClick={() => { dismissVideoPublication(task.id); navigate(`/create?type=video&draft=${task.draftId}`); }} aria-label="المحاولة مجدداً"><RotateCcw size={18} /></button>
      ) : complete ? (
        <button type="button" className="video-publish-action" onClick={() => dismissVideoPublication(task.id)} aria-label="إغلاق"><XCircle size={18} /></button>
      ) : <Film size={18} className="video-publish-mark" />}
    </aside>
    {reviewOpen && review && <DetailDialog title="مراجعة نتيجة عزل الصوت" theme={theme} language="ar" onClose={() => setReviewOpen(false)} busy={cancelBusy} className="video-audio-review"><video controls playsInline src={task.previewUrl}/><p>العزل تجريبي وقد تبقى آثار من الموسيقى. استمع إلى الفيديو كاملاً، وانشره فقط إذا وافق شروط التطبيق.</p><div><button onClick={() => { approveVideoPublication(task.id); setReviewOpen(false); }}>راجعت الصوت · نشر الفيديو</button><button disabled={cancelBusy} onClick={cancel}>إلغاء والاحتفاظ بالمسودة</button></div></DetailDialog>}
    </>
  );
}
